import { useMemo, useState } from 'react';
import { PageHeader, Panel, Toolbar } from '../components/Page';
import { EmptyState, ErrorState, TableSkeleton } from '../components/Feedback';
import { Chip, EntityTypeChip } from '../components/Chips';
import { SelectField, TextField } from '../components/Form';
import { TableWrap } from '../components/Table';
import { SortableTh } from '../components/SortableTh';
import { Pagination } from '../components/Pagination';
import { useAuth } from '../lib/auth';
import { transactions } from '../lib/endpoints';
import { useAsync, useDebounced } from '../lib/hooks';
import { useSortableTable } from '../lib/useSortableTable';
import { useToast } from '../lib/toast';
import { errorMessage } from '../lib/api';
import { QboEntityType, QboEntityTypeLabels } from '../lib/enums';
import { formatDate, formatMoney, formatRelative } from '../lib/format';
import type { PagedResult, QboTransactionDto } from '../lib/types';

/** This page covers the two spend entity types only. */
const SPEND_TYPES: QboEntityType[] = [QboEntityType.Purchase, QboEntityType.BillPayment];

type ExpenseColumn = 'reference' | 'type' | 'vendor' | 'date' | 'amount' | 'balance' | 'origin';

function expenseSortValue(txn: QboTransactionDto, key: ExpenseColumn): string | number {
  switch (key) {
    case 'reference':
      return txn.docNumber ?? '';
    case 'type':
      return txn.entityType;
    case 'vendor':
      return txn.vendorName ?? '';
    case 'date':
      return txn.txnDate ?? '';
    case 'amount':
      return txn.totalAmount ?? 0;
    case 'balance':
      return txn.balance ?? 0;
    case 'origin':
      return txn.linkedToGateway ? 1 : 0;
    default:
      return '';
  }
}

export function ExpensesPaymentsPage(): JSX.Element {
  const { can } = useAuth();
  const toast = useToast();
  const canPull = can('transactions.pull');

  const [entityType, setEntityType] = useState<QboEntityType | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [pulling, setPulling] = useState<QboEntityType | null>(null);
  const debouncedSearch = useDebounced(search, 350);

  /**
   * The API filters by a single entityType. With "All" selected both spend types are fetched and
   * merged client-side so the page never shows bills or purchase orders.
   */
  const list = useAsync(
    async (signal): Promise<PagedResult<QboTransactionDto>> => {
      if (entityType !== null) {
        return transactions.list({ entityType, search: debouncedSearch, page, pageSize }, signal);
      }
      const [purchases, payments] = await Promise.all(
        SPEND_TYPES.map((type) =>
          transactions.list({ entityType: type, search: debouncedSearch, page, pageSize }, signal),
        ),
      );
      const items = [...purchases.items, ...payments.items].sort((a, b) => {
        const left = a.txnDate ? Date.parse(a.txnDate) : 0;
        const right = b.txnDate ? Date.parse(b.txnDate) : 0;
        return right - left;
      });
      const totalCount = purchases.totalCount + payments.totalCount;
      return {
        items,
        page,
        pageSize,
        totalCount,
        totalPages: Math.max(purchases.totalPages, payments.totalPages),
      };
    },
    [entityType, debouncedSearch, page, pageSize],
  );

  const table = useSortableTable<ExpenseColumn>({
    storageKey: 'expensesPaymentsColumnWidths',
    columns: {
      reference: { minWidth: 130 },
      type: { minWidth: 110 },
      vendor: { minWidth: 140 },
      date: { minWidth: 110 },
      amount: { minWidth: 100 },
      balance: { minWidth: 100 },
      origin: { minWidth: 130 },
    },
  });

  const typeOptions = useMemo(
    () => SPEND_TYPES.map((type) => ({ value: String(type), label: QboEntityTypeLabels[type] })),
    [],
  );

  const pull = async (type: QboEntityType) => {
    setPulling(type);
    try {
      const response = await transactions.pull({
        entityType: type,
        changedSinceUtc: null,
        maxRecords: null,
      });
      toast.success(
        `${QboEntityTypeLabels[type]} pulled`,
        `${response.recordsPulled} read · ${response.recordsCreated} new · ${response.recordsUpdated} updated.`,
      );
      list.reload();
    } catch (error) {
      toast.error('Pull failed', errorMessage(error));
    } finally {
      setPulling(null);
    }
  };

  return (
    <>
      <PageHeader
        title="Expenses & Payments"
        description="Cached QuickBooks purchase (expense) and bill payment transactions."
        actions={
          canPull ? (
            <>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => pull(QboEntityType.Purchase)}
                disabled={pulling !== null}
              >
                {pulling === QboEntityType.Purchase ? 'Pulling…' : 'Pull expenses'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => pull(QboEntityType.BillPayment)}
                disabled={pulling !== null}
              >
                {pulling === QboEntityType.BillPayment ? 'Pulling…' : 'Pull payments'}
              </button>
            </>
          ) : undefined
        }
      />

      <Panel>
        <Toolbar>
          <TextField
            label="Search"
            type="search"
            value={search}
            onChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
            placeholder="Document number, vendor or memo"
            className="toolbar-grow"
          />
          <SelectField
            label="Type"
            value={entityType === null ? '' : String(entityType)}
            onChange={(value) => {
              setEntityType(value === '' ? null : (Number(value) as QboEntityType));
              setPage(1);
            }}
            options={typeOptions}
            placeholder="Expenses and payments"
          />
        </Toolbar>

        {list.loading && !list.data ? (
          <TableSkeleton rows={10} columns={7} />
        ) : list.error ? (
          <ErrorState message="Transactions could not be loaded." detail={list.error} onRetry={list.reload} />
        ) : !list.data || list.data.items.length === 0 ? (
          <EmptyState
            title="Nothing cached yet"
            description="Pull expenses or payments from QuickBooks to populate this list."
            action={
              canPull ? (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => pull(QboEntityType.Purchase)}
                  disabled={pulling !== null}
                >
                  Pull expenses
                </button>
              ) : undefined
            }
          />
        ) : (
          <>
            <TableWrap busy={list.loading} tableClassName="data-table-sortable">
              <colgroup>
                <col style={table.columnStyle('reference')} />
                <col style={table.columnStyle('type')} />
                <col style={table.columnStyle('vendor')} />
                <col style={table.columnStyle('date')} />
                <col style={table.columnStyle('amount')} />
                <col style={table.columnStyle('balance')} />
                <col style={table.columnStyle('origin')} />
              </colgroup>
              <thead>
                <tr>
                  <SortableTh
                    label="Reference"
                    sortKey="reference"
                    currentSortKey={table.sortKey}
                    sortDir={table.sortDir}
                    onSort={table.toggleSort}
                    style={table.columnStyle('reference')}
                    onResizeStart={table.beginResize('reference')}
                  />
                  <SortableTh
                    label="Type"
                    sortKey="type"
                    currentSortKey={table.sortKey}
                    sortDir={table.sortDir}
                    onSort={table.toggleSort}
                    style={table.columnStyle('type')}
                    onResizeStart={table.beginResize('type')}
                  />
                  <SortableTh
                    label="Vendor"
                    sortKey="vendor"
                    currentSortKey={table.sortKey}
                    sortDir={table.sortDir}
                    onSort={table.toggleSort}
                    style={table.columnStyle('vendor')}
                    onResizeStart={table.beginResize('vendor')}
                  />
                  <SortableTh
                    label="Date"
                    sortKey="date"
                    currentSortKey={table.sortKey}
                    sortDir={table.sortDir}
                    onSort={table.toggleSort}
                    style={table.columnStyle('date')}
                    onResizeStart={table.beginResize('date')}
                  />
                  <SortableTh
                    label="Amount"
                    sortKey="amount"
                    currentSortKey={table.sortKey}
                    sortDir={table.sortDir}
                    onSort={table.toggleSort}
                    className="num"
                    style={table.columnStyle('amount')}
                    onResizeStart={table.beginResize('amount')}
                  />
                  <SortableTh
                    label="Balance"
                    sortKey="balance"
                    currentSortKey={table.sortKey}
                    sortDir={table.sortDir}
                    onSort={table.toggleSort}
                    className="num"
                    style={table.columnStyle('balance')}
                    onResizeStart={table.beginResize('balance')}
                  />
                  <SortableTh
                    label="Origin"
                    sortKey="origin"
                    currentSortKey={table.sortKey}
                    sortDir={table.sortDir}
                    onSort={table.toggleSort}
                    style={table.columnStyle('origin')}
                    onResizeStart={table.beginResize('origin')}
                  />
                </tr>
              </thead>
              <tbody>
                {/* The "all" view merges two API pages, so the index keeps keys unique. */}
                {table.sort(list.data.items, expenseSortValue).map((txn, index) => (
                  <tr key={`${txn.entityType}-${txn.id}-${index}`}>
                    <td>
                      <span className="cell-strong">{txn.docNumber ?? '—'}</span>
                      <span className="cell-sub mono">{txn.qboId}</span>
                    </td>
                    <td>
                      <EntityTypeChip entityType={txn.entityType} />
                    </td>
                    <td>
                      {txn.vendorName ?? '—'}
                      {txn.memo ? <span className="cell-sub">{txn.memo}</span> : null}
                    </td>
                    <td>
                      {formatDate(txn.txnDate)}
                      {txn.dueDate ? <span className="cell-sub">due {formatDate(txn.dueDate)}</span> : null}
                    </td>
                    <td className="num">{formatMoney(txn.totalAmount, txn.currencyCode)}</td>
                    <td className="num">{formatMoney(txn.balance, txn.currencyCode)}</td>
                    <td>
                      {txn.linkedToGateway ? (
                        <Chip tone="good">Gateway</Chip>
                      ) : (
                        <Chip tone="neutral">QuickBooks</Chip>
                      )}
                      <span className="cell-sub">synced {formatRelative(txn.lastSyncedUtc)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>

            <Pagination
              page={list.data.page}
              pageSize={list.data.pageSize}
              totalCount={list.data.totalCount}
              totalPages={list.data.totalPages}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          </>
        )}
      </Panel>
    </>
  );
}
